import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Save, X, GripVertical, Truck, Settings } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import AdminPageHeader from '../../components/AdminPageHeader';
import axiosInstance from '../../api/axios';
import { toast } from 'sonner';
import { formatEUR } from '../../utils/format';

const ShippingSettings = () => {
  const [shippingMethods, setShippingMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    free_shipping_threshold: '',
    estimated_days: '',
    is_active: true
  });

  useEffect(() => {
    fetchShippingMethods();
  }, []);

  const fetchShippingMethods = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/shipping?active_only=false');
      setShippingMethods(response.data);
    } catch (error) {
      console.error('Error fetching shipping methods:', error);
      toast.error('Fehler beim Laden der Versandarten');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        free_shipping_threshold: formData.free_shipping_threshold ? parseFloat(formData.free_shipping_threshold) : null
      };

      if (editingId) {
        await axiosInstance.put(`/shipping/${editingId}`, payload);
        toast.success('Versandart aktualisiert');
      } else {
        await axiosInstance.post('/shipping', payload);
        toast.success('Versandart erstellt');
      }

      resetForm();
      fetchShippingMethods();
    } catch (error) {
      console.error('Error saving shipping method:', error);
      toast.error('Fehler beim Speichern');
    }
  };

  const handleEdit = (method) => {
    setEditingId(method.id);
    setFormData({
      name: method.name,
      description: method.description || '',
      price: method.price.toString(),
      free_shipping_threshold: method.free_shipping_threshold?.toString() || '',
      estimated_days: method.estimated_days || '',
      is_active: method.is_active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie diese Versandart wirklich löschen?')) return;

    try {
      await axiosInstance.delete(`/shipping/${id}`);
      toast.success('Versandart gelöscht');
      fetchShippingMethods();
    } catch (error) {
      console.error('Error deleting shipping method:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      free_shipping_threshold: '',
      estimated_days: '',
      is_active: true
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F5EDE4' }}>
        <p className="text-gray-600">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 sm:py-6 lg:py-8 px-3 sm:px-4 md:px-6 lg:px-8" style={{ backgroundColor: '#F5EDE4' }}>
      <div className="max-w-6xl mx-auto">
        <AdminPageHeader 
          title="Versandkosten-Verwaltung" 
          subtitle="Versandarten erstellen und verwalten"
          icon={Truck}
        />
        
        {/* Section: Aktionen - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center justify-between" style={{ backgroundColor: '#C4A084' }}>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-[#F0FFF0]">
              <Plus className="h-4 w-4 text-[#60B060]" />
            </div>
            <h2 className="text-base font-semibold text-white">Aktionen</h2>
          </div>
          {!showAddForm && (
            <Button onClick={() => setShowAddForm(true)} className="bg-white text-[#C4A084] hover:bg-white/90 rounded-none">
              <Plus className="h-4 w-4 mr-2" />
              Neue Versandart
            </Button>
          )}
        </div>

        {/* Add/Edit Form */}
        {showAddForm && (
          <Card className="mb-4 sm:mb-6 rounded-none">
            <CardHeader className="bg-amber-50/50">
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-amber-600" />
                {editingId ? 'Versandart bearbeiten' : 'Neue Versandart'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Name *</label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="z.B. Standard, Express"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Preis (€) *</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      placeholder="z.B. 4.99"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Kostenloser Versand ab (€)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.free_shipping_threshold}
                      onChange={(e) => setFormData({ ...formData, free_shipping_threshold: e.target.value })}
                      placeholder="z.B. 50.00 (Optional)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Lieferzeit</label>
                    <Input
                      value={formData.estimated_days}
                      onChange={(e) => setFormData({ ...formData, estimated_days: e.target.value })}
                      placeholder="z.B. 2-3 Werktage"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-1">Beschreibung</label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Zusätzliche Informationen (Optional)"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="rounded"
                    />
                    <label htmlFor="is_active" className="text-sm font-medium">Aktiv</label>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button type="submit">
                    <Save className="h-4 w-4 mr-2" />
                    {editingId ? 'Aktualisieren' : 'Erstellen'}
                  </Button>
                  <Button type="button" variant="outline" onClick={resetForm}>
                    <X className="h-4 w-4 mr-2" />
                    Abbrechen
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Section: Shipping Methods - Goldbraune Überschrift */}
        <div className="mb-3 p-2 px-3 flex items-center gap-2 mt-4" style={{ backgroundColor: '#C4A084' }}>
          <div className="w-6 h-6 rounded flex items-center justify-center bg-[#E0FFFF]">
            <Truck className="h-4 w-4 text-[#40A0A0]" />
          </div>
          <h2 className="text-base font-semibold text-white">Versandarten-Liste</h2>
        </div>

        {/* Shipping Methods Table */}
        {shippingMethods.length === 0 ? (
          <Card className="rounded-none">
            <CardContent className="py-12 text-center">
              <p className="text-gray-600 mb-4">Noch keine Versandarten angelegt</p>
              <Button onClick={() => setShowAddForm(true)} className="bg-[#C4A484] hover:bg-[#B89968] text-white rounded-none">
                <Plus className="h-4 w-4 mr-2" />
                Erste Versandart erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-none">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-teal-50/50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Preis</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Kostenlos ab</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Lieferzeit</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {shippingMethods.map((method) => (
                      <tr key={method.id} className="hover:bg-teal-50/30">
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-gray-900">{method.name}</div>
                            {method.description && (
                              <div className="text-sm text-gray-500">{method.description}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="bg-emerald-50/70 text-emerald-700 px-2 py-1 rounded-none text-sm font-semibold">
                            {formatEUR(method.price)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {method.free_shipping_threshold ? (
                            <span className="bg-violet-50/70 text-violet-700 px-2 py-1 rounded-none text-sm">
                              {formatEUR(method.free_shipping_threshold)}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{method.estimated_days || '-'}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-none ${
                            method.is_active ? 'bg-emerald-50/70 text-emerald-700' : 'bg-slate-50/70 text-slate-700'
                          }`}>
                            {method.is_active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(method)}
                              className="rounded-none bg-sky-50/70 border-sky-200 hover:bg-sky-100 text-sky-700"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(method.id)}
                              className="rounded-none bg-red-50/70 border-red-200 hover:bg-red-100 text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ShippingSettings;
